import type { Dict } from "#/lib/i18n";
import { whatsAppLink } from "#/lib/message-templates";

/**
 * The two floating contact buttons every landing template shares. They only
 * render when the training has the matching field set, so a training without a
 * Messenger page simply doesn't get the button.
 */

function MessengerIcon({
	className,
	title,
}: {
	className?: string;
	title: string;
}) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-labelledby="msgr-cta-title"
		>
			<title id="msgr-cta-title">{title}</title>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M12 2C6.24 2 2 6.22 2 11.92c0 2.98 1.19 5.55 3.14 7.32.16.15.26.35.27.57l.05 1.78c.02.57.61.94 1.13.71l1.98-.88c.17-.08.36-.09.54-.04.91.25 1.88.39 2.82.39 5.76 0 10-4.22 10-9.92C22 6.22 17.76 2 12 2Zm6 7.7-2.94 4.67c-.47.74-1.47.93-2.18.4l-2.34-1.76a.6.6 0 0 0-.72 0l-3.16 2.4c-.42.32-.97-.18-.69-.63l2.94-4.66c.47-.75 1.47-.93 2.18-.4l2.34 1.75c.21.16.5.16.72 0l3.16-2.39c.42-.32.97.18.69.62Z"
			/>
		</svg>
	);
}

export function MessengerFloat({
	t,
	messengerUrl,
}: {
	t: Dict;
	messengerUrl: string;
}) {
	return (
		<div className="fixed bottom-5 left-5 z-50 h-14 w-14">
			<span
				aria-hidden
				className="absolute inset-0 rounded-full bg-[#0084FF] animate-wa-ring"
			/>
			<a
				href={messengerUrl}
				target="_blank"
				rel="noreferrer"
				aria-label={t.messenger.aria}
				data-track="messenger_float"
				data-track-type="contact"
				data-track-section="floating"
				data-track-label="messenger"
				className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#0084FF] text-white shadow-lift animate-wa-pump transition-transform hover:scale-110 hover:[animation-play-state:paused]"
			>
				<MessengerIcon className="h-7 w-7" title={t.messenger.title} />
				<span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[0.6rem] font-bold text-white ring-2 ring-canvas">
					1
				</span>
			</a>
		</div>
	);
}

function WhatsAppIcon({
	className,
	title,
}: {
	className?: string;
	title: string;
}) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-labelledby="wa-cta-title"
		>
			<title id="wa-cta-title">{title}</title>
			<path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.79.47 3.46 1.29 4.9L2 22l5.31-1.39a9.86 9.86 0 0 0 4.73 1.2h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.13-2.9-7C17.17 3.03 14.69 2 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.15.83.84-3.07-.19-.31a8.23 8.23 0 0 1-1.26-4.38c0-4.55 3.7-8.24 8.26-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.42 5.83c0 4.55-3.7 8.25-8.25 8.25Zm4.52-6.18c-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.13-.17.24-.64.81-.78.97-.14.17-.29.19-.53.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05s.88 2.38 1 2.55c.13.17 1.73 2.64 4.2 3.7.59.25 1.05.41 1.4.52.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.23-.17-.48-.29Z" />
		</svg>
	);
}

export function WhatsAppFloat({
	t,
	whatsappNumber,
}: {
	t: Dict;
	whatsappNumber: string;
}) {
	const href = whatsAppLink(whatsappNumber, t.whatsapp.prefill);
	return (
		<div className="fixed bottom-5 right-5 z-50 h-14 w-14">
			<span
				aria-hidden
				className="absolute inset-0 rounded-full bg-[#25D366] animate-wa-ring"
			/>
			<a
				href={href}
				target="_blank"
				rel="noreferrer"
				aria-label={t.whatsapp.aria}
				data-track="whatsapp_float"
				data-track-type="contact"
				data-track-section="floating"
				data-track-label="whatsapp"
				className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lift animate-wa-pump transition-transform hover:scale-110 hover:[animation-play-state:paused]"
			>
				<WhatsAppIcon className="h-7 w-7" title={t.whatsapp.title} />
			</a>
		</div>
	);
}
